
import { supabase } from './supabaseClient';
import { Produto, Bairro, Categoria, AdicionalCategoria, AdicionalItem } from '../types';

/**
 * SQL PARA ATUALIZAÇÃO DO BANCO DE DADOS (Execute no Editor SQL do Supabase):
 * 
 * -- 1. Adicionar coluna 'ativo' em categorias
 * ALTER TABLE public.categorias ADD COLUMN IF NOT EXISTS ativo boolean DEFAULT true;
 * 
 * -- 2. Adicionar coluna 'ativo' em categorias de adicionais (grupos)
 * ALTER TABLE public.adicionais_categorias ADD COLUMN IF NOT EXISTS ativo boolean DEFAULT true;
 * 
 * -- 3. Tabela de relacionamento de produtos com adicionais
 * CREATE TABLE IF NOT EXISTS public.produtos_adicionais_rel (
 *   produto_id uuid REFERENCES public.produtos(id) ON DELETE CASCADE,
 *   categoria_adicional_id uuid REFERENCES public.adicionais_categorias(id) ON DELETE CASCADE,
 *   PRIMARY KEY (produto_id, categoria_adicional_id)
 * );
 */

export const produtosService = {
  async uploadImage(file: File): Promise<string> {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}-${Math.floor(Math.random() * 1000)}.${fileExt}`;
    const { error } = await supabase.storage.from('images').upload(fileName, file);
    if (error) throw error;
    const { data } = supabase.storage.from('images').getPublicUrl(fileName);
    return data.publicUrl;
  },

  async getProdutos(empresaId: string): Promise<Produto[]> {
    const { data } = await supabase
      .from('produtos')
      .select('*, produtos_adicionais_rel(categoria_adicional_id)')
      .eq('empresa_id', empresaId)
      .order('nome', { ascending: true });
    
    return (data || []).map(p => ({
      ...p,
      adicionais_ids: (p as any).produtos_adicionais_rel?.map((r: any) => r.categoria_adicional_id) || []
    }));
  },

  async salvarProduto(produto: Partial<Produto>) {
    const { adicionais_ids, produtos_adicionais_rel, ...dadosProduto } = produto as any;
    let prodId = produto.id;

    if (prodId) {
      const { id, ...updateData } = dadosProduto;
      const { error } = await supabase.from('produtos').update(updateData).eq('id', id);
      if (error) throw error;
    } else {
      const { data, error } = await supabase.from('produtos').insert([dadosProduto]).select().single();
      if (error) throw error;
      prodId = data.id;
    }

    if (adicionais_ids !== undefined && prodId) {
      await supabase.from('produtos_adicionais_rel').delete().eq('produto_id', prodId);
      if (adicionais_ids.length > 0) {
        const relations = adicionais_ids.map((catId: string) => ({ 
          produto_id: prodId, 
          categoria_adicional_id: catId 
        }));
        const { error: relError } = await supabase.from('produtos_adicionais_rel').insert(relations);
        if (relError) throw relError;
      }
    }
  },

  async deletarProduto(id: string) {
    return await supabase.from('produtos').delete().eq('id', id);
  },

  async getBairros(empresaId: string): Promise<Bairro[]> {
    const { data } = await supabase
      .from('bairros')
      .select('*')
      .eq('empresa_id', empresaId)
      .order('nome', { ascending: true });
    return data || [];
  },

  async salvarBairro(bairro: Partial<Bairro>) {
    if (bairro.id) {
      const { id, ...updateData } = bairro;
      return await supabase.from('bairros').update(updateData).eq('id', id);
    }
    return await supabase.from('bairros').insert([bairro]);
  },

  async deletarBairro(id: string) {
    return await supabase.from('bairros').delete().eq('id', id);
  },

  async getCategorias(empresaId: string): Promise<Categoria[]> {
    const { data } = await supabase
      .from('categorias')
      .select('*')
      .eq('empresa_id', empresaId)
      .order('ordem', { ascending: true });
    return data || [];
  },

  async salvarCategoria(categoria: Partial<Categoria>) {
    const { ...dadosCategoria } = categoria as any;
    if (categoria.id) {
      const { id, ...updateData } = dadosCategoria;
      return await supabase.from('categorias').update(updateData).eq('id', id);
    }
    return await supabase.from('categorias').insert([dadosCategoria]);
  },

  async deletarCategoria(id: string) {
    return await supabase.from('categorias').delete().eq('id', id);
  },

  async getAdicionaisCategorias(empresaId: string): Promise<AdicionalCategoria[]> {
    const { data } = await supabase
      .from('adicionais_categorias')
      .select('*, adicionais_itens(*)')
      .eq('empresa_id', empresaId);
    
    return (data || []).map(cat => ({
      ...cat,
      itens: (cat as any).adicionais_itens || []
    }));
  },

  async salvarAdicionalCategoria(categoria: Partial<AdicionalCategoria>) {
    // Removemos propriedades de relacionamento que não são colunas na tabela principal
    const { itens, adicionais_itens, ...dadosCategoria } = categoria as any;
    
    if (categoria.id) {
      const { id, ...updateData } = dadosCategoria;
      // Garante que os campos booleanos sejam incluídos se estiverem presentes no objeto de atualização
      return await supabase.from('adicionais_categorias').update(updateData).eq('id', id);
    }
    
    return await supabase.from('adicionais_categorias').insert([dadosCategoria]);
  },

  async deletarAdicionalCategoria(id: string) {
    return await supabase.from('adicionais_categorias').delete().eq('id', id);
  },

  async salvarAdicionalItem(item: Partial<AdicionalItem>) {
    if (item.id) {
      const { id, ...updateData } = item;
      return await supabase.from('adicionais_itens').update(updateData).eq('id', id);
    }
    return await supabase.from('adicionais_itens').insert([item]);
  },

  async deletarAdicionalItem(id: string) {
    return await supabase.from('adicionais_itens').delete().eq('id', id);
  }
};
